import { useMemo } from "react";
import type { CommunicationStateRow } from "../api";
import type { ExplodedErrorRow } from "../types";

/**
 * Преобразует строки состояния связи в "взорванный" формат:
 * одна ошибка = одна строка таблицы
 */
export function useExplodedErrors(data: CommunicationStateRow[] | undefined): ExplodedErrorRow[] {
  return useMemo((): ExplodedErrorRow[] => {
    if (!data) {
      return [];
    }

    const result: ExplodedErrorRow[] = [];

    for (const row of data) {
      const hasCommErrors = (row.comm_errors ?? 0) > 0;
      const hasProtocolErrors = (row.protocol_errors ?? 0) > 0;
      const hasSysError = Boolean(row.sys_error);

      if (!hasCommErrors && !hasProtocolErrors && !hasSysError) {
        continue;
      }

      // Если есть расшифрованные ошибки - создаём отдельную строку для каждой
      if (row.sys_error_descriptions && row.sys_error_descriptions.length > 0) {
        for (const desc of row.sys_error_descriptions) {
          // Парсим "E-1: Восстановление питания" → code="E-1", description="Восстановление питания"
          const colonIdx = desc.indexOf(":");
          const errorCode = colonIdx > 0 ? desc.slice(0, colonIdx).trim() : desc;
          const errorDesc = colonIdx > 0 ? desc.slice(colonIdx + 1).trim() : "";

          result.push({
            ...row,
            error_code: errorCode,
            error_description: errorDesc,
            row_key: `${row.meter_id ?? row.place_id ?? "0"}-${errorCode}`,
          });
        }
      } else if (hasSysError) {
        result.push({
          ...row,
          error_code: row.sys_error ?? undefined,
          row_key: `${row.meter_id ?? row.place_id ?? "0"}-sys`,
        });
      } else if (hasCommErrors || hasProtocolErrors) {
        // Ошибки связи/протокола без sys_error
        result.push({
          ...row,
          row_key: `${row.meter_id ?? row.place_id ?? "0"}-comm`,
        });
      }
    }

    return result;
  }, [data]);
}
